import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  Package, 
  Plus, 
  Pencil, 
  Trash2, 
  Tag,
  Boxes,
  X,
  Cloud,
  ShoppingBag,
  Search,
  Layers,
  AlertCircle,
  Check,
  Store,
  Users,
  ChevronDown,
  IceCream,
  Shirt
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import { ProductThumbnail } from "../components/ProductThumbnail";
import { API, useAuth } from "../App";

const MerchProductsPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = user?.role === "admin";
  
  // ============ PRODUCTOS (internal products with margins) STATE ============
  const [internalProducts, setInternalProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "",
    cost_percentage: "",
    heladeria_margin_percentage: "",
    merch_margin_percentage: "",
    color: "#F59E0B",
    is_merch: false,
    is_proveedor: false,
    subcategory_id: null,
    track_inventory: false,
    proveedor_payout_type: "",
    proveedor_payout_value: ""
  });
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedProductForAssign, setSelectedProductForAssign] = useState(null);
  const [selectedImportedIds, setSelectedImportedIds] = useState([]);
  const [expandedProductId, setExpandedProductId] = useState(null);

  // ============ SUBCATEGORIES STATE ============
  const [subcategories, setSubcategories] = useState([]);
  const [newSubcatName, setNewSubcatName] = useState("");
  const [newSubcatParent, setNewSubcatParent] = useState("heladeria");
  const [collapsedSubcats, setCollapsedSubcats] = useState(() => {
    try {
      const saved = localStorage.getItem("merchProducts.collapsedSubcats");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const saved = localStorage.getItem("merchProducts.collapsedSections");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [productSearch, setProductSearch] = useState("");

  // Persist collapsed state to localStorage so it survives reloads
  useEffect(() => {
    try { localStorage.setItem("merchProducts.collapsedSubcats", JSON.stringify(collapsedSubcats)); } catch (e) { /* ignore */ }
  }, [collapsedSubcats]);
  useEffect(() => {
    try { localStorage.setItem("merchProducts.collapsedSections", JSON.stringify(collapsedSections)); } catch (e) { /* ignore */ }
  }, [collapsedSections]);

  // ============ IMPORTED PRODUCTS STATE (Odoo/Shopify) ============
  const [importedProducts, setImportedProducts] = useState([]);
  const [importedLoading, setImportedLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSource, setFilterSource] = useState("all");

  // ============ MERCH PRODUCTS STATE (for batches/SKUs) ============
  const [merchProducts, setMerchProducts] = useState([]);
  const [merchLoading, setMerchLoading] = useState(true);
  const [merchDialogOpen, setMerchDialogOpen] = useState(false);
  const [editMerchDialogOpen, setEditMerchDialogOpen] = useState(false);
  const [selectedMerchProduct, setSelectedMerchProduct] = useState(null);
  const [merchForm, setMerchForm] = useState({
    name: "",
    description: "",
    sku_list: []
  });
  const [editMerchForm, setEditMerchForm] = useState({
    name: "",
    description: "",
    sku_list: []
  });
  const [newSku, setNewSku] = useState("");
  const [editNewSku, setEditNewSku] = useState("");

  // ============ FETCH DATA ============
  const fetchInternalProducts = async () => {
    try {
      setProductsLoading(true);
      const response = await axios.get(`${API}/margin-categories`, getAuthHeaders());
      setInternalProducts(response.data);
    } catch (error) {
      toast.error("Error al cargar productos");
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API}/subcategories`, getAuthHeaders());
      setSubcategories(response.data);
    } catch (error) {
      console.error("Error fetching subcategories", error);
    }
  };

  const fetchImportedProducts = async () => {
    try {
      setImportedLoading(true);
      const response = await axios.get(`${API}/products`, getAuthHeaders());
      setImportedProducts(response.data);
    } catch (error) {
      toast.error("Error al cargar productos importados");
    } finally {
      setImportedLoading(false);
    }
  };

  const fetchMerchProducts = async () => {
    try {
      setMerchLoading(true);
      const response = await axios.get(`${API}/merch-products`, getAuthHeaders());
      setMerchProducts(response.data);
    } catch (error) {
      toast.error("Error al cargar productos de merch");
    } finally {
      setMerchLoading(false);
    }
  };

  useEffect(() => {
    fetchInternalProducts();
    fetchImportedProducts();
    fetchMerchProducts();
    fetchSubcategories();
  }, []);

  // ============ INTERNAL PRODUCTS HANDLERS ============
  const resetProductForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: "",
      cost_percentage: "",
      heladeria_margin_percentage: "",
      merch_margin_percentage: "",
      color: "#F59E0B",
      is_merch: false,
      is_proveedor: false,
      subcategory_id: null,
      track_inventory: false,
      proveedor_payout_type: "",
      proveedor_payout_value: ""
    });
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    
    const total = parseFloat(productForm.cost_percentage || 0) + 
                  parseFloat(productForm.heladeria_margin_percentage || 0) + 
                  parseFloat(productForm.merch_margin_percentage || 0);
    
    if (Math.abs(total - 100) > 0.01) {
      toast.error("Los porcentajes deben sumar 100%");
      return;
    }

    try {
      const payload = {
        name: productForm.name,
        cost_percentage: parseFloat(productForm.cost_percentage),
        heladeria_margin_percentage: parseFloat(productForm.heladeria_margin_percentage),
        merch_margin_percentage: parseFloat(productForm.merch_margin_percentage),
        color: productForm.color,
        is_merch: productForm.is_merch,
        is_proveedor: productForm.is_proveedor,
        subcategory_id: productForm.subcategory_id,
        track_inventory: !!productForm.track_inventory,
        proveedor_payout_type: productForm.is_proveedor ? (productForm.proveedor_payout_type || null) : null,
        proveedor_payout_value: productForm.is_proveedor && productForm.proveedor_payout_type
          ? parseFloat(productForm.proveedor_payout_value || 0)
          : 0
      };

      if (editingProduct) {
        await axios.put(`${API}/margin-categories/${editingProduct.id}`, payload, getAuthHeaders());
        toast.success("Producto actualizado");
      } else {
        await axios.post(`${API}/margin-categories`, payload, getAuthHeaders());
        toast.success("Producto creado");
      }
      setProductDialogOpen(false);
      resetProductForm();
      fetchInternalProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar producto");
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      cost_percentage: product.cost_percentage?.toString() || "",
      heladeria_margin_percentage: product.heladeria_margin_percentage?.toString() || "",
      merch_margin_percentage: product.merch_margin_percentage?.toString() || "",
      color: product.color || "#F59E0B",
      is_merch: product.is_merch || false,
      is_proveedor: product.is_proveedor || false,
      subcategory_id: product.subcategory_id || null,
      track_inventory: product.track_inventory || false,
      proveedor_payout_type: product.proveedor_payout_type || "",
      proveedor_payout_value: product.proveedor_payout_value?.toString() || ""
    });
    setProductDialogOpen(true);
  };

  const handleUnlinkProductFromInternal = async (importedProductId) => {
    if (!editingProduct) return;
    try {
      await axios.delete(
        `${API}/margin-categories/${editingProduct.id}/products/${importedProductId}`,
        getAuthHeaders()
      );
      toast.success("Producto desvinculado");
      // Optimistically update the editingProduct in-place so the modal list refreshes
      const newIds = (editingProduct.product_ids || []).filter(id => id !== importedProductId);
      setEditingProduct({ ...editingProduct, product_ids: newIds });
      fetchInternalProducts();
      fetchImportedProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al desvincular producto");
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm("¿Eliminar este producto? Los productos importados asignados quedarán sin agrupar.")) return;
    
    try {
      await axios.delete(`${API}/margin-categories/${productId}`, getAuthHeaders());
      toast.success("Producto eliminado");
      fetchInternalProducts();
      fetchImportedProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar producto");
    }
  };

  const handleCreateSubcategory = async (parent) => {
    if (!newSubcatName.trim()) return;
    try {
      await axios.post(`${API}/subcategories`, { name: newSubcatName.trim(), parent }, getAuthHeaders());
      toast.success(`Subcategoría "${newSubcatName}" creada`);
      setNewSubcatName("");
      fetchSubcategories();
    } catch (error) {
      toast.error("Error al crear subcategoría");
    }
  };

  const handleDeleteSubcategory = async (subcatId) => {
    if (!window.confirm("¿Eliminar esta subcategoría? Los productos quedarán sin agrupar.")) return;
    try {
      await axios.delete(`${API}/subcategories/${subcatId}`, getAuthHeaders());
      toast.success("Subcategoría eliminada");
      fetchSubcategories();
      fetchInternalProducts();
    } catch (error) {
      toast.error("Error al eliminar subcategoría");
    }
  };


  const openAssignDialog = (product) => {
    setSelectedProductForAssign(product);
    setSelectedImportedIds(product.product_ids || []);
    setAssignDialogOpen(true);
  };

  const handleDeleteImportedProduct = async (product) => {
    const skuInfo = product.variant_count
      ? ` (${product.variant_count} variantes)`
      : product.sku ? ` (SKU ${product.sku})` : "";
    const msg = `¿Eliminar "${product.name}"${skuInfo} del catálogo local?\n\nEsto NO lo borra de Odoo ni Shopify, solo lo quita de esta app. Se removerá también de cualquier producto interno donde esté asignado.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await axios.delete(`${API}/products/${product.id}`, getAuthHeaders());
      const invMsg = r.data?.inventory_levels_removed
        ? ` (${r.data.inventory_levels_removed} filas de inventario limpiadas)`
        : "";
      toast.success(`"${product.name}" eliminado${invMsg}`);
      fetchImportedProducts();
      fetchInternalProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar producto");
    }
  };

  const toggleImportedSelection = (productId) => {
    setSelectedImportedIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSaveAssignments = async () => {
    try {
      await axios.post(
        `${API}/margin-categories/${selectedProductForAssign.id}/products`,
        selectedImportedIds,
        getAuthHeaders()
      );
      toast.success("Productos asignados correctamente");
      setAssignDialogOpen(false);
      fetchInternalProducts();
      fetchImportedProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar productos");
    }
  };

  // Get imported products that are not assigned to any internal product
  const unassignedImported = importedProducts.filter(p => !p.margin_category_id);

  // Quick assign: assign a single imported product to an internal product
  const handleQuickAssign = async (importedProductId, internalProductId) => {
    if (!internalProductId || internalProductId === "none") {
      // Remove from current assignment
      const currentInternal = internalProducts.find(p => p.product_ids?.includes(importedProductId));
      if (currentInternal) {
        const newIds = currentInternal.product_ids.filter(id => id !== importedProductId);
        try {
          await axios.post(`${API}/margin-categories/${currentInternal.id}/products`, newIds, getAuthHeaders());
          toast.success(`Producto desasignado de ${currentInternal.name}`);
          fetchInternalProducts();
          fetchImportedProducts();
        } catch (error) {
          toast.error("Error al desasignar producto");
        }
      }
      return;
    }
    
    // Remove from old assignment if any
    const oldInternal = internalProducts.find(p => p.product_ids?.includes(importedProductId));
    if (oldInternal && oldInternal.id !== internalProductId) {
      const oldIds = oldInternal.product_ids.filter(id => id !== importedProductId);
      await axios.post(`${API}/margin-categories/${oldInternal.id}/products`, oldIds, getAuthHeaders());
    }
    
    // Add to new internal product
    const targetInternal = internalProducts.find(p => p.id === internalProductId);
    const newIds = [...(targetInternal?.product_ids || []).filter(id => id !== importedProductId), importedProductId];
    try {
      await axios.post(`${API}/margin-categories/${internalProductId}/products`, newIds, getAuthHeaders());
      toast.success(`Asignado a ${targetInternal?.name}`);
      fetchInternalProducts();
      fetchImportedProducts();
    } catch (error) {
      toast.error("Error al asignar producto");
    }
  };

  // ============ MERCH PRODUCTS HANDLERS ============
  const handleAddSku = () => {
    if (newSku.trim() && !merchForm.sku_list.includes(newSku.trim().toUpperCase())) {
      setMerchForm({
        ...merchForm,
        sku_list: [...merchForm.sku_list, newSku.trim().toUpperCase()]
      });
      setNewSku("");
    }
  };

  const handleRemoveSku = (skuToRemove) => {
    setMerchForm({
      ...merchForm,
      sku_list: merchForm.sku_list.filter(sku => sku !== skuToRemove)
    });
  };

  const handleEditAddSku = () => {
    if (editNewSku.trim() && !editMerchForm.sku_list.includes(editNewSku.trim().toUpperCase())) {
      setEditMerchForm({
        ...editMerchForm,
        sku_list: [...editMerchForm.sku_list, editNewSku.trim().toUpperCase()]
      });
      setEditNewSku("");
    }
  };

  const handleEditRemoveSku = (skuToRemove) => {
    setEditMerchForm({
      ...editMerchForm,
      sku_list: editMerchForm.sku_list.filter(sku => sku !== skuToRemove)
    });
  };

  const handleCreateMerchProduct = async (e) => {
    e.preventDefault();
    
    if (!merchForm.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      await axios.post(`${API}/merch-products`, merchForm, getAuthHeaders());
      toast.success("Producto creado correctamente");
      setMerchDialogOpen(false);
      setMerchForm({ name: "", description: "", sku_list: [] });
      setNewSku("");
      fetchMerchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear producto");
    }
  };

  const openEditMerchDialog = (product) => {
    setSelectedMerchProduct(product);
    setEditMerchForm({
      name: product.name,
      description: product.description || "",
      sku_list: [...product.sku_list]
    });
    setEditMerchDialogOpen(true);
  };

  const handleUpdateMerchProduct = async (e) => {
    e.preventDefault();
    
    if (!editMerchForm.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      await axios.put(`${API}/merch-products/${selectedMerchProduct.id}`, editMerchForm, getAuthHeaders());
      toast.success("Producto actualizado correctamente");
      setEditMerchDialogOpen(false);
      fetchMerchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar producto");
    }
  };

  const handleDeleteMerchProduct = async (productId) => {
    if (!window.confirm("¿Estás seguro de eliminar este producto?")) return;

    try {
      await axios.delete(`${API}/merch-products/${productId}`, getAuthHeaders());
      toast.success("Producto eliminado");
      fetchMerchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar producto");
    }
  };

  // Filter imported products
  const filteredImported = importedProducts.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSource = filterSource === "all" || 
      (filterSource === "odoo" && product.source === "odoo") ||
      (filterSource === "shopify" && product.source === "shopify") ||
      (filterSource === "unassigned" && !product.margin_category_id);
    return matchesSearch && matchesSource;
  });

  // Stats for imported
  const importedStats = {
    total: importedProducts.length,
    odoo: importedProducts.filter(p => p.source === "odoo").length,
    shopify: importedProducts.filter(p => p.source === "shopify").length,
    unassigned: unassignedImported.length
  };

  // Find internal product name for an imported product
  const getInternalProductName = (importedId) => {
    const internalProd = internalProducts.find(p => p.product_ids?.includes(importedId));
    return internalProd ? internalProd.name : null;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="merch-products-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#4A4A4A] font-['Manrope']">Productos</h1>
          <p className="text-[#6B7280]">Crea productos y asigna los productos importados de Odoo/Shopify</p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="bg-[#FEF3C7] border-[#F59E0B]/30 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="products" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-white" data-testid="products-tab">
              <Package className="w-4 h-4 mr-2" />
              Productos
            </TabsTrigger>
            <TabsTrigger value="imported" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-white" data-testid="imported-tab">
              <Layers className="w-4 h-4 mr-2" />
              Importados (Odoo/Shopify)
            </TabsTrigger>
            <TabsTrigger value="merch" className="data-[state=active]:bg-[#F59E0B] data-[state=active]:text-white" data-testid="merch-tab">
              <Boxes className="w-4 h-4 mr-2" />
              Productos Merch (Lotes)
            </TabsTrigger>
          </TabsList>

          {/* ============ PRODUCTOS TAB ============ */}
          <TabsContent value="products" className="space-y-4">
            {/* Header with search and create */}
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input
                  placeholder="Buscar producto..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-9 h-9 bg-white border-gray-200 text-sm"
                  data-testid="product-search-input"
                />
              </div>
              <Button 
                onClick={() => { resetProductForm(); setProductDialogOpen(true); }} 
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white h-9"
                data-testid="new-product-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo
              </Button>
            </div>

            {/* Products Grid - Divided by business */}
            {productsLoading ? (
              <div className="text-center py-12 text-[#6B7280]">Cargando productos...</div>
            ) : internalProducts.length === 0 ? (
              <Card className="bg-white border-[#F59E0B]/30">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 mx-auto text-[#6B7280]/30 mb-4" />
                  <h3 className="text-lg font-medium text-[#4A4A4A]">No hay productos</h3>
                  <p className="text-[#6B7280] mt-1">Crea un producto para empezar a agrupar productos importados</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* HELADERÍA Section */}
                {(() => {
                  const heladeriaProducts = internalProducts.filter(p => !p.is_merch);
                  const merchProducts = internalProducts.filter(p => p.is_merch);
                  
                  const renderProductCard = (product) => {
                    const isExpanded = expandedProductId === product.id;
                    return (
                      <Card key={product.id} className="bg-white border-l-4 hover:shadow-sm transition-shadow" style={{ borderLeftColor: product.color }}>
                        <CardContent className="p-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <button 
                              onClick={() => setExpandedProductId(isExpanded ? null : product.id)}
                              className="flex items-center gap-2 flex-1 min-w-0 text-left"
                            >
                              <ChevronDown className={`w-4 h-4 text-[#6B7280] shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              <ProductThumbnail image_url={product.image_url} name={product.name} size="sm" />
                              <span className="font-semibold text-[#4A4A4A] truncate">{product.name}</span>
                              {product.is_proveedor && (
                                <Badge className="bg-[#8B5CF6] text-white text-[10px] px-1.5 py-0 shrink-0">Proveedor</Badge>
                              )}
                            </button>
                            <div className="flex items-center gap-3 text-xs font-mono shrink-0">
                              <span className="text-[#EF4444]">C:{product.cost_percentage}%</span>
                              <span className="text-[#10B981]">H:{product.ice_cream_shop_margin_percentage}%</span>
                              <span className="text-[#F59E0B]">M:{product.merch_margin_percentage}%</span>
                            </div>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0" style={{ backgroundColor: product.color + '15', color: product.color, borderColor: product.color + '40' }}>
                              {product.product_ids?.length || 0} asignados
                            </Badge>
                            <div className="flex gap-0.5 shrink-0">
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleEditProduct(product); }} className="h-7 w-7 p-0 text-[#6B7280] hover:text-[#4A4A4A]">
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteProduct(product.id); }} className="h-7 w-7 p-0 text-[#6B7280] hover:text-[#EF4444]">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
                              {product.product_ids?.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-xs text-[#6B7280] mb-1">Productos importados asignados:</p>
                                  <div className="flex flex-wrap gap-1">
                                    {product.product_ids.map(pid => {
                                      const imported = importedProducts.find(p => p.id === pid);
                                      return imported ? (
                                        <Badge key={pid} variant="outline" className="text-[10px] px-1.5 py-0">
                                          {imported.source === 'shopify' && <ShoppingBag className="w-3 h-3 mr-1" />}
                                          {imported.source === 'odoo' && <Cloud className="w-3 h-3 mr-1" />}
                                          {imported.name}
                                        </Badge>
                                      ) : null;
                                    })}
                                  </div>
                                </div>
                              )}
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => openAssignDialog(product)}
                                className="w-full border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#FEF3C7] h-8 text-xs"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Asignar Productos Importados
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  };

                  const renderSection = (products, parentType, icon, iconBg, iconColor, title) => {
                    // Apply search filter
                    const filtered = productSearch.trim() 
                      ? products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()))
                      : products;
                    
                    const parentSubcats = subcategories.filter(sc => sc.parent === parentType);
                    const parentSubcatIds = new Set(parentSubcats.map(sc => sc.id));
                    const withSubcat = {};
                    const withoutSubcat = [];
                    
                    filtered.forEach(p => {
                      // If product has a subcategory_id but it doesn't belong to this parent section
                      // (e.g. Heladería product toggled to Merch keeping the old subcategory_id),
                      // show it under "Sin categoría" so it never disappears.
                      if (p.subcategory_id && parentSubcatIds.has(p.subcategory_id)) {
                        if (!withSubcat[p.subcategory_id]) withSubcat[p.subcategory_id] = [];
                        withSubcat[p.subcategory_id].push(p);
                      } else {
                        withoutSubcat.push(p);
                      }
                    });

                    const isSectionCollapsed = collapsedSections[parentType];

                    return (
                      <div>
                        <button 
                          onClick={() => setCollapsedSections(prev => ({...prev, [parentType]: !prev[parentType]}))}
                          className="flex items-center gap-2 mb-3 w-full text-left group"
                        >
                          <ChevronDown className={`w-4 h-4 text-[#4A4A4A] transition-transform ${isSectionCollapsed ? '-rotate-90' : ''}`} />
                          <div className={`w-6 h-6 rounded-md ${iconBg} flex items-center justify-center`}>
                            {icon}
                          </div>
                          <h3 className="text-sm font-bold text-[#4A4A4A] uppercase tracking-wide">{title}</h3>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-[#6B7280]">{filtered.length}{productSearch ? `/${products.length}` : ''}</Badge>
                          {/* Inline add subcategory */}
                          <div className="flex items-center gap-1 ml-auto" onClick={(e) => e.stopPropagation()}>
                            <Input
                              placeholder="+ subcategoría"
                              value={newSubcatParent === parentType ? newSubcatName : ""}
                              onFocus={() => setNewSubcatParent(parentType)}
                              onChange={(e) => { setNewSubcatParent(parentType); setNewSubcatName(e.target.value); }}
                              className="h-7 text-xs w-32 bg-white border-gray-200"
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreateSubcategory(parentType); }}}
                            />
                            <Button 
                              variant="ghost" size="sm" 
                              onClick={() => handleCreateSubcategory(parentType)}
                              disabled={!newSubcatName.trim() || newSubcatParent !== parentType}
                              className="h-7 w-7 p-0 text-[#10B981]"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </button>
                        {!isSectionCollapsed && (
                        <div>
                          {/* Products grouped by subcategory */}
                          {parentSubcats.map(sc => {
                            const scProducts = withSubcat[sc.id] || [];
                            const isCollapsed = collapsedSubcats[sc.id];
                            return (
                              <div key={sc.id} className="pl-4 border-l-2 border-gray-200">
                                <button 
                                  onClick={() => setCollapsedSubcats(prev => ({...prev, [sc.id]: !prev[sc.id]}))}
                                  className="flex items-center gap-2 mb-1.5 w-full text-left group"
                                >
                                  <ChevronDown className={`w-3 h-3 text-[#6B7280] transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                                  <Tag className="w-3 h-3 text-[#6B7280]" />
                                  <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider">{sc.name}</span>
                                  <Badge variant="outline" className="text-[9px] px-1 py-0 text-[#9CA3AF]">{scProducts.length}</Badge>
                                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteSubcategory(sc.id); }} className="h-5 w-5 p-0 text-[#9CA3AF] hover:text-[#EF4444] ml-auto opacity-0 group-hover:opacity-100">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </button>
                                {!isCollapsed && (
                                  <div className="space-y-1.5">
                                    {scProducts.length > 0 ? scProducts.map(renderProductCard) : (
                                      <p className="text-xs text-[#D1D5DB] pl-5 italic">Sin productos</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {/* Products without subcategory */}
                          {withoutSubcat.length > 0 && (
                            <div className={parentSubcats.length > 0 ? "pl-4 border-l-2 border-dashed border-gray-200" : ""}>
                              {parentSubcats.length > 0 && (
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider">Sin subcategoría</span>
                                  <Badge variant="outline" className="text-[9px] px-1 py-0 text-[#9CA3AF]">{withoutSubcat.length}</Badge>
                                </div>
                              )}
                              <div className="space-y-1.5">
                                {withoutSubcat.map(renderProductCard)}
                              </div>
                            </div>
                          )}
                          {filtered.length === 0 && (
                            <p className="text-sm text-[#9CA3AF] pl-4">{productSearch ? 'Sin resultados' : `No hay productos de ${title}`}</p>
                          )}
                        </div>
                        )}
                      </div>
                    );
                  };

                  return (
                    <>
                      {renderSection(heladeriaProducts, "heladeria", <IceCream className="w-3.5 h-3.5 text-[#E11D48]" />, "bg-[#FECDD3]", "#E11D48", "Heladería")}
                      {renderSection(merchProducts, "merch", <Shirt className="w-3.5 h-3.5 text-[#F59E0B]" />, "bg-[#FEF3C7]", "#F59E0B", "Merch")}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Unassigned Products Alert */}
            {unassignedImported.length > 0 && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">
                        {unassignedImported.length} producto{unassignedImported.length !== 1 ? 's' : ''} importado{unassignedImported.length !== 1 ? 's' : ''} sin asignar
                      </p>
                      <p className="text-sm text-amber-700">
                        Ve a la pestaña "Importados (Odoo/Shopify)" para verlos.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ============ IMPORTADOS TAB ============ */}
          <TabsContent value="imported" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white border-[#E5E7EB]">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#4A4A4A]">{importedStats.total}</p>
                  <p className="text-sm text-[#6B7280]">Total</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-[#8B5CF6]/30">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#8B5CF6]">{importedStats.odoo}</p>
                  <p className="text-sm text-[#6B7280]">Odoo</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-[#95BF47]/30">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-[#95BF47]">{importedStats.shopify}</p>
                  <p className="text-sm text-[#6B7280]">Shopify</p>
                </CardContent>
              </Card>
              <Card className="bg-white border-amber-300">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{importedStats.unassigned}</p>
                  <p className="text-sm text-[#6B7280]">Sin Asignar</p>
                </CardContent>
              </Card>
            </div>

            {/* Filters */}
            <Card className="bg-white border-[#F59E0B]/30">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                    <Input
                      placeholder="Buscar productos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-white border-[#F59E0B]/30"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant={filterSource === "all" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFilterSource("all")}
                      className={filterSource === "all" ? "bg-[#F59E0B] hover:bg-[#D97706]" : ""}
                    >
                      Todos
                    </Button>
                    <Button
                      variant={filterSource === "odoo" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFilterSource("odoo")}
                      className={filterSource === "odoo" ? "bg-[#8B5CF6] hover:bg-[#7C3AED]" : "border-[#8B5CF6]/30 text-[#8B5CF6]"}
                    >
                      <Cloud className="w-4 h-4 mr-1" /> Odoo
                    </Button>
                    <Button
                      variant={filterSource === "shopify" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFilterSource("shopify")}
                      className={filterSource === "shopify" ? "bg-[#95BF47] hover:bg-[#5E8E3E]" : "border-[#95BF47]/30 text-[#95BF47]"}
                    >
                      <ShoppingBag className="w-4 h-4 mr-1" /> Shopify
                    </Button>
                    <Button
                      variant={filterSource === "unassigned" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFilterSource("unassigned")}
                      className={filterSource === "unassigned" ? "bg-amber-500 hover:bg-amber-600" : "border-amber-300 text-amber-600"}
                    >
                      <AlertCircle className="w-4 h-4 mr-1" /> Sin Asignar
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-[#9CA3AF] mt-2">{filteredImported.length} de {importedProducts.length} productos</p>
              </CardContent>
            </Card>

            {/* Products Grid */}
            {importedLoading ? (
              <div className="text-center py-12 text-[#6B7280]">Cargando productos importados...</div>
            ) : filteredImported.length === 0 ? (
              <Card className="bg-white border-[#F59E0B]/30">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 mx-auto text-[#6B7280]/30 mb-4" />
                  <h3 className="text-lg font-medium text-[#4A4A4A]">No hay productos importados</h3>
                  <p className="text-[#6B7280] mt-1">
                    {searchTerm || filterSource !== "all" ? "No se encontraron productos con esos filtros" : "Importa productos desde Configuración > Odoo o Shopify"}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredImported.map((product) => {
                  const assignedTo = getInternalProductName(product.id);
                  const hasVariants = product.variants && product.variants.length > 0;
                  return (
                    <Card key={product.id} className={`bg-white border-[#E5E7EB] hover:shadow-md transition-shadow ${!assignedTo ? 'ring-2 ring-amber-200' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              product.source === 'shopify' ? 'bg-[#95BF47]/20' :
                              product.source === 'odoo' ? 'bg-[#8B5CF6]/20' : 'bg-[#6B7280]/20'
                            }`}>
                              {product.source === 'shopify' ? <ShoppingBag className="w-5 h-5 text-[#95BF47]" /> :
                               product.source === 'odoo' ? <Cloud className="w-5 h-5 text-[#8B5CF6]" /> :
                               <Package className="w-5 h-5 text-[#6B7280]" />}
                            </div>
                            <div>
                              <h3 className="text-[#4A4A4A] font-medium text-sm">{product.name}</h3>
                              <div className="flex items-center gap-1 mt-1 flex-wrap">
                                {product.source === 'odoo' && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/30">Odoo</Badge>}
                                {product.source === 'shopify' && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[#95BF47]/10 text-[#95BF47] border-[#95BF47]/30">Shopify</Badge>}
                                {product.category && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600">{product.category}</Badge>}
                                {product.is_active === false && <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-red-50 text-red-600 border-red-200">Archivado</Badge>}
                              </div>
                            </div>
                          </div>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDeleteImportedProduct(product); }}
                              className="h-7 w-7 p-0 text-[#9CA3AF] hover:text-[#EF4444] hover:bg-[#FEE2E2] flex-shrink-0"
                              title="Eliminar producto del catálogo local"
                              data-testid={`delete-imported-product-${product.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        {/* SKU del producto base */}
                        {product.sku && (
                          <div className="mb-2">
                            <span className="text-[10px] text-[#9CA3AF]">SKU: </span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-600 border-blue-200">
                              <Tag className="w-2.5 h-2.5 mr-1" />{product.sku}
                            </Badge>
                          </div>
                        )}

                        {/* Variantes y sus SKUs */}
                        {hasVariants && (
                          <div className="mb-3 p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-1 mb-2">
                              <Layers className="w-3 h-3 text-[#6B7280]" />
                              <span className="text-[10px] font-medium text-[#6B7280]">{product.variant_count || product.variants.length} variantes</span>
                            </div>
                            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                              {product.variants.map((variant, idx) => (
                                <Badge key={idx} variant="outline" className="text-[9px] px-1.5 py-0.5 bg-white text-[#4A4A4A] border-gray-200">
                                  <span className="font-medium">{variant.name || 'Variante'}</span>
                                  {variant.sku && (
                                    <span className="ml-1 text-blue-600">
                                      <Tag className="w-2 h-2 inline mr-0.5" />{variant.sku}
                                    </span>
                                  )}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Assignment Select */}
                        <Select
                          value={(() => {
                            const internal = internalProducts.find(p => p.product_ids?.includes(product.id));
                            return internal ? internal.id : "none";
                          })()}
                          onValueChange={(val) => handleQuickAssign(product.id, val)}
                        >
                          <SelectTrigger 
                            className={`h-8 text-xs ${assignedTo ? 'bg-[#10B981]/10 border-[#10B981]/30 text-[#059669]' : 'bg-amber-50 border-amber-300 text-amber-700'}`}
                            data-testid={`assign-select-${product.id}`}
                          >
                            <SelectValue placeholder="Asignar a..." />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="none" className="text-amber-600 text-xs">Sin asignar</SelectItem>
                            {internalProducts.map((ip) => (
                              <SelectItem key={ip.id} value={ip.id} className="text-xs">
                                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: ip.color }}></span>
                                {ip.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ============ MERCH PRODUCTS TAB ============ */}
          <TabsContent value="merch" className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#6B7280]">
                  Define productos con SKUs para trackear ventas en lotes de producción
                </p>
              </div>
              <Button onClick={() => setMerchDialogOpen(true)} className="bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="new-merch-product-button">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Producto Merch
              </Button>
            </div>

            {/* Merch Products Grid */}
            {merchLoading ? (
              <div className="text-center py-12 text-[#6B7280]">Cargando productos de merch...</div>
            ) : merchProducts.length === 0 ? (
              <Card className="bg-white border-[#F59E0B]/30">
                <CardContent className="p-12 text-center">
                  <Boxes className="w-16 h-16 mx-auto text-[#6B7280]/30 mb-4" />
                  <h3 className="text-lg font-medium text-[#4A4A4A]">No hay productos de merch</h3>
                  <p className="text-[#6B7280] mt-1">Crea un producto para comenzar a trackear ventas por SKU</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {merchProducts.map((product) => (
                  <Card key={product.id} className="bg-white border-[#F59E0B]/30 hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#F59E0B]/20 flex items-center justify-center">
                            <Boxes className="w-5 h-5 text-[#F59E0B]" />
                          </div>
                          <div>
                            <h3 className="text-[#4A4A4A] font-medium">{product.name}</h3>
                            <span className="text-xs text-[#6B7280]">{product.sku_list?.length || 0} SKUs</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditMerchDialog(product)} className="text-[#6B7280] hover:text-[#4A4A4A]"><Pencil className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteMerchProduct(product.id)} className="text-[#6B7280] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      {product.description && <p className="text-sm text-[#6B7280] mb-3">{product.description}</p>}
                      <div className="flex flex-wrap gap-1">
                        {product.sku_list?.map((sku, index) => (
                          <Badge key={index} variant="outline" className="text-[10px] px-1.5 py-0 bg-[#FEF3C7] text-[#B45309] border-[#F59E0B]/30">
                            <Tag className="w-3 h-3 mr-1" />{sku}
                          </Badge>
                        ))}
                      </div>
                      <Button variant="outline" size="sm" onClick={() => navigate(`/merch/costs?product=${product.id}`)} className="w-full mt-3 border-[#F59E0B]/30 text-[#F59E0B] hover:bg-[#FEF3C7]">
                        Ver Lotes
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ============ DIALOGS ============ */}

        {/* Create/Edit Product Dialog */}
        <Dialog open={productDialogOpen} onOpenChange={(open) => { setProductDialogOpen(open); if (!open) resetProductForm(); }}>
          <DialogContent className="bg-white border-[#FECDD3] text-[#4A4A4A] max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                {editingProduct ? "Editar Producto" : "Nuevo Producto"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProduct} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Nombre del Producto</Label>
                <Input
                  value={productForm.name}
                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                  placeholder="Ej: Darkarzilla"
                  required
                  className="bg-[#FEF3C7]/30 border-[#F59E0B]/30"
                />
              </div>

              {/* Margins */}
              <div className="bg-[#FEF3C7]/30 rounded-lg p-4 space-y-3 border border-[#F59E0B]/30">
                <p className="text-[#92400E] text-sm font-medium">Distribución de porcentajes (deben sumar 100%)</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#6B7280]/70 text-xs">% Costo</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={productForm.cost_percentage}
                      onChange={(e) => setProductForm({...productForm, cost_percentage: e.target.value})}
                      placeholder="42"
                      required
                      className="bg-white border-[#F59E0B]/30 font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#6B7280]/70 text-xs">% Heladería</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={productForm.heladeria_margin_percentage}
                      onChange={(e) => setProductForm({...productForm, heladeria_margin_percentage: e.target.value})}
                      placeholder="29"
                      required
                      className="bg-white border-[#F59E0B]/30 font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#6B7280]/70 text-xs">% Merch</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={productForm.merch_margin_percentage}
                      onChange={(e) => setProductForm({...productForm, merch_margin_percentage: e.target.value})}
                      placeholder="29"
                      required
                      className="bg-white border-[#F59E0B]/30 font-mono"
                    />
                  </div>
                </div>
                <div className="text-center">
                  <span className={`text-sm font-mono ${
                    Math.abs((parseFloat(productForm.cost_percentage || 0) + parseFloat(productForm.heladeria_margin_percentage || 0) + parseFloat(productForm.merch_margin_percentage || 0)) - 100) < 0.01
                      ? 'text-[#10B981]' : 'text-[#EF4444]'
                  }`}>
                    Total: {(parseFloat(productForm.cost_percentage || 0) + parseFloat(productForm.heladeria_margin_percentage || 0) + parseFloat(productForm.merch_margin_percentage || 0)).toFixed(2)}%
                  </span>
                </div>
              </div>

              {/* is_merch Toggle */}
              <div className="bg-[#F59E0B]/10 rounded-lg p-4 border border-[#F59E0B]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Store className="w-5 h-5 text-[#F59E0B]" />
                    <div>
                      <Label className="text-[#4A4A4A] font-medium">Producto de Merch</Label>
                      <p className="text-xs text-[#6B7280]">Activar para incluir ventas en el Dashboard de Merch</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="is-merch-toggle"
                    checked={productForm.is_merch}
                    onCheckedChange={(checked) => setProductForm({
                      ...productForm,
                      is_merch: checked,
                      // Auto-clear subcategory when switching sections so the product
                      // never gets stuck under a subcategory that doesn't belong to
                      // the new parent (which used to make it disappear from the UI).
                      subcategory_id: null,
                    })}
                    className="data-[state=checked]:bg-[#F59E0B]"
                  />
                </div>
              </div>

              {/* track_inventory Toggle - only relevant for Heladería products */}
              {!productForm.is_merch && (
                <div className="bg-[#3B82F6]/10 rounded-lg p-4 border border-[#3B82F6]/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-[#3B82F6]" />
                      <div>
                        <Label className="text-[#4A4A4A] font-medium">Rastrear inventario</Label>
                        <p className="text-xs text-[#6B7280]">Actívalo para que este producto de Heladería aparezca en la sección de Inventario (sin volverlo Merch)</p>
                      </div>
                    </div>
                    <Switch
                      data-testid="track-inventory-toggle"
                      checked={!!productForm.track_inventory}
                      onCheckedChange={(checked) => setProductForm({...productForm, track_inventory: checked})}
                      className="data-[state=checked]:bg-[#3B82F6]"
                    />
                  </div>
                </div>
              )}

              {/* is_proveedor Toggle */}
              <div className="bg-[#8B5CF6]/10 rounded-lg p-4 border border-[#8B5CF6]/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-[#8B5CF6]" />
                    <div>
                      <Label className="text-[#4A4A4A] font-medium">Producto de Proveedor</Label>
                      <p className="text-xs text-[#6B7280]">Si está activo, el % Merch se suma a Proveedores en el Dashboard</p>
                    </div>
                  </div>
                  <Switch
                    checked={productForm.is_proveedor}
                    onCheckedChange={(checked) => setProductForm({...productForm, is_proveedor: checked})}
                    className="data-[state=checked]:bg-[#8B5CF6]"
                  />
                </div>

                {/* Proveedor payout scheme (only when is_proveedor=true) */}
                {productForm.is_proveedor && (
                  <div className="mt-4 pt-4 border-t border-[#8B5CF6]/20 space-y-3">
                    <div>
                      <Label className="text-[#4A4A4A] font-medium text-sm">Esquema de pago al proveedor</Label>
                      <p className="text-xs text-[#6B7280] mb-2">Cómo se calcula lo que se le paga al proveedor por cada venta.</p>
                      <Select
                        value={productForm.proveedor_payout_type || "legacy"}
                        onValueChange={(val) => setProductForm({
                          ...productForm,
                          proveedor_payout_type: val === "legacy" ? "" : val
                        })}
                      >
                        <SelectTrigger className="bg-white border-gray-300" data-testid="proveedor-payout-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="legacy">Por defecto (% Merch sobre venta)</SelectItem>
                          <SelectItem value="margin_percent">% de la utilidad (venta − costo)</SelectItem>
                          <SelectItem value="revenue_percent">% del precio de venta bruto</SelectItem>
                          <SelectItem value="fixed_per_piece">Monto fijo por pieza vendida</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {productForm.proveedor_payout_type && (
                      <div>
                        <Label className="text-[#6B7280] text-sm">
                          {productForm.proveedor_payout_type === "fixed_per_piece"
                            ? "Monto por pieza (MXN)"
                            : "Porcentaje (%)"}
                        </Label>
                        <Input
                          type="number"
                          step={productForm.proveedor_payout_type === "fixed_per_piece" ? "0.01" : "0.1"}
                          min="0"
                          value={productForm.proveedor_payout_value}
                          onChange={(e) => setProductForm({...productForm, proveedor_payout_value: e.target.value})}
                          placeholder={productForm.proveedor_payout_type === "fixed_per_piece" ? "Ej. 80" : "Ej. 50"}
                          className="bg-white border-gray-300"
                          data-testid="proveedor-payout-value-input"
                        />
                        <p className="text-xs text-[#6B7280] mt-1">
                          {productForm.proveedor_payout_type === "margin_percent" && "Se calcula sobre (precio − costo) de cada venta."}
                          {productForm.proveedor_payout_type === "revenue_percent" && "Se calcula sobre el precio de venta bruto (sin descontar costos ni fees)."}
                          {productForm.proveedor_payout_type === "fixed_per_piece" && "Cantidad fija que se paga por cada unidad vendida, independiente del precio."}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Subcategory Selector */}
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Subcategoría</Label>
                <Select
                  value={productForm.subcategory_id || "none"}
                  onValueChange={(val) => setProductForm({...productForm, subcategory_id: val === "none" ? null : val})}
                >
                  <SelectTrigger className="bg-white border-gray-300">
                    <SelectValue placeholder="Sin subcategoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">Sin subcategoría</SelectItem>
                    {subcategories
                      .filter(sc => productForm.is_merch ? sc.parent === "merch" : sc.parent === "heladeria")
                      .map(sc => (
                        <SelectItem key={sc.id} value={sc.id}>{sc.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Color Picker */}
              <div className="space-y-2">
                <Label className="text-[#6B7280]">Color</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={productForm.color}
                    onChange={(e) => setProductForm({...productForm, color: e.target.value})}
                    className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                  />
                  <Input
                    value={productForm.color}
                    onChange={(e) => setProductForm({...productForm, color: e.target.value})}
                    placeholder="#F59E0B"
                    className="flex-1 bg-white border-gray-300 font-mono"
                  />
                </div>
              </div>

              {/* Linked Imported Products (only when editing) */}
              {editingProduct && (
                <div className="space-y-2" data-testid="linked-products-section">
                  <div className="flex items-center justify-between">
                    <Label className="text-[#6B7280]">
                      Productos vinculados (Odoo/Shopify)
                      <span className="ml-2 text-xs text-[#9CA3AF]">
                        {(editingProduct.product_ids || []).length} asignado{(editingProduct.product_ids || []).length !== 1 ? "s" : ""}
                      </span>
                    </Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setProductDialogOpen(false);
                        openAssignDialog(editingProduct);
                      }}
                      className="h-8 border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10"
                      data-testid="open-assign-dialog-from-edit"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Agregar
                    </Button>
                  </div>
                  {(editingProduct.product_ids || []).length === 0 ? (
                    <div className="text-xs text-[#9CA3AF] italic p-3 border border-dashed border-[#E5E7EB] rounded-md text-center">
                      Sin productos vinculados. Toca &quot;Agregar&quot; para asignar productos importados.
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto border border-[#E5E7EB] rounded-md divide-y">
                      {(editingProduct.product_ids || []).map((pid) => {
                        const p = importedProducts.find(ip => ip.id === pid);
                        return (
                          <div
                            key={pid}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-[#FEF3C7]/30"
                            data-testid={`linked-product-row-${pid}`}
                          >
                            <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${
                              p?.source === 'shopify' ? 'bg-[#95BF47]/20' :
                              p?.source === 'odoo' ? 'bg-[#8B5CF6]/20' : 'bg-gray-100'
                            }`}>
                              {p?.source === 'shopify' ? <ShoppingBag className="w-3 h-3 text-[#95BF47]" /> :
                               p?.source === 'odoo' ? <Cloud className="w-3 h-3 text-[#8B5CF6]" /> :
                               <Package className="w-3 h-3 text-gray-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#4A4A4A] truncate">
                                {p?.name || <span className="italic text-[#9CA3AF]">Producto no encontrado ({pid.slice(0, 8)}…)</span>}
                              </p>
                              {p?.source && (
                                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">{p.source}</p>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUnlinkProductFromInternal(pid)}
                              className="h-7 w-7 p-0 text-[#9CA3AF] hover:text-[#EF4444] hover:bg-[#FEE2E2]"
                              title="Desvincular"
                              data-testid={`unlink-product-${pid}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white">
                  {editingProduct ? "Actualizar" : "Guardar"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Assign Products Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent className="bg-white text-[#4A4A4A] max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">
                Asignar Productos a "{selectedProductForAssign?.name}"
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#6B7280] -mt-2">
              Selecciona los productos importados de Odoo/Shopify que pertenecen a este producto
            </p>
            <div className="flex-1 overflow-y-auto border rounded-lg mt-4">
              {importedProducts.length === 0 ? (
                <div className="p-8 text-center text-[#6B7280]">
                  No hay productos importados. Importa productos desde Configuración.
                </div>
              ) : (
                <div className="divide-y">
                  {importedProducts.map((product) => {
                    const isSelected = selectedImportedIds.includes(product.id);
                    const currentAssignment = getInternalProductName(product.id);
                    const isAssignedElsewhere = currentAssignment && currentAssignment !== selectedProductForAssign?.name;
                    
                    return (
                      <div 
                        key={product.id} 
                        className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-[#FEF3C7]/50' : ''}`}
                        onClick={() => toggleImportedSelection(product.id)}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          isSelected ? 'bg-[#F59E0B] border-[#F59E0B]' : 'border-gray-300'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <div className={`w-8 h-8 rounded flex items-center justify-center ${
                          product.source === 'shopify' ? 'bg-[#95BF47]/20' :
                          product.source === 'odoo' ? 'bg-[#8B5CF6]/20' : 'bg-gray-100'
                        }`}>
                          {product.source === 'shopify' ? <ShoppingBag className="w-4 h-4 text-[#95BF47]" /> :
                           product.source === 'odoo' ? <Cloud className="w-4 h-4 text-[#8B5CF6]" /> :
                           <Package className="w-4 h-4 text-gray-500" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{product.name}</p>
                          {isAssignedElsewhere && (
                            <p className="text-xs text-amber-600">Actualmente en: {currentAssignment}</p>
                          )}
                        </div>
                        {product.source === 'odoo' && <Badge variant="outline" className="text-[10px] bg-[#8B5CF6]/10 text-[#8B5CF6]">Odoo</Badge>}
                        {product.source === 'shopify' && <Badge variant="outline" className="text-[10px] bg-[#95BF47]/10 text-[#95BF47]">Shopify</Badge>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-4 border-t mt-4">
              <Button type="button" variant="outline" onClick={() => setAssignDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleSaveAssignments} className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white">
                Guardar ({selectedImportedIds.length} productos)
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create Merch Product Dialog */}
        <Dialog open={merchDialogOpen} onOpenChange={setMerchDialogOpen}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-[#4A4A4A]">Crear Producto de Merch</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateMerchProduct} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Producto</Label>
                <Input value={merchForm.name} onChange={(e) => setMerchForm({ ...merchForm, name: e.target.value })} placeholder="Ej: Playera Darkar Negra" className="bg-white border-[#FECDD3]" />
              </div>
              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Textarea value={merchForm.description} onChange={(e) => setMerchForm({ ...merchForm, description: e.target.value })} placeholder="Descripción del producto..." className="bg-white border-[#FECDD3]" />
              </div>
              <div className="space-y-2">
                <Label>SKUs de Variantes</Label>
                <p className="text-xs text-[#6B7280]">Agrega los SKUs de todas las variantes (tallas, colores, etc.)</p>
                <div className="flex gap-2">
                  <Input value={newSku} onChange={(e) => setNewSku(e.target.value.toUpperCase())} placeholder="Ej: PDN-M" className="bg-white border-[#FECDD3]" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSku())} />
                  <Button type="button" onClick={handleAddSku} variant="outline" className="border-[#F59E0B] text-[#F59E0B]"><Plus className="w-4 h-4" /></Button>
                </div>
                {merchForm.sku_list.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 p-2 bg-[#FEF3C7]/30 rounded-lg">
                    {merchForm.sku_list.map((sku, index) => (
                      <Badge key={index} className="bg-[#F59E0B] text-white flex items-center gap-1 px-2 py-1">
                        <Tag className="w-3 h-3" />{sku}
                        <button type="button" onClick={() => handleRemoveSku(sku)} className="ml-1 hover:bg-[#D97706] rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setMerchDialogOpen(false)} className="flex-1">Cancelar</Button>
                <Button type="submit" className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white">Crear Producto</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Merch Product Dialog */}
        <Dialog open={editMerchDialogOpen} onOpenChange={setEditMerchDialogOpen}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-[#4A4A4A]">Editar Producto de Merch</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateMerchProduct} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del Producto</Label>
                <Input value={editMerchForm.name} onChange={(e) => setEditMerchForm({ ...editMerchForm, name: e.target.value })} className="bg-white border-[#FECDD3]" />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea value={editMerchForm.description} onChange={(e) => setEditMerchForm({ ...editMerchForm, description: e.target.value })} className="bg-white border-[#FECDD3]" />
              </div>
              <div className="space-y-2">
                <Label>SKUs de Variantes</Label>
                <div className="flex gap-2">
                  <Input value={editNewSku} onChange={(e) => setEditNewSku(e.target.value.toUpperCase())} placeholder="Agregar SKU" className="bg-white border-[#FECDD3]" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleEditAddSku())} />
                  <Button type="button" onClick={handleEditAddSku} variant="outline" className="border-[#F59E0B] text-[#F59E0B]"><Plus className="w-4 h-4" /></Button>
                </div>
                {editMerchForm.sku_list.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2 p-2 bg-[#FEF3C7]/30 rounded-lg">
                    {editMerchForm.sku_list.map((sku, index) => (
                      <Badge key={index} className="bg-[#F59E0B] text-white flex items-center gap-1 px-2 py-1">
                        <Tag className="w-3 h-3" />{sku}
                        <button type="button" onClick={() => handleEditRemoveSku(sku)} className="ml-1 hover:bg-[#D97706] rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditMerchDialogOpen(false)} className="flex-1">Cancelar</Button>
                <Button type="submit" className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white">Actualizar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MerchProductsPage;
